import type { CompanyUser } from "@/apis/gql/types";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";

import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
} from "@/components/ui/select";
import { TypographySmall } from "@/components/ui/typography";
import { CompanyRole } from "@/lib/miscellaneous/authorization";
import { SystemRole, getUserTypeFromCompanyRole } from "@/lib/users/user-roles";
import { cn } from "@/lib/utils";
import { useUpdateCompanyUser } from "@/queries/companies/users/update-user-role";

const getInitials = (name: string) => {
  return name
    .split(" ")
    .map((word) => word[0])
    .splice(0, 2)
    .join("");
};

const formSchema = z.object({
  userId: z.string().min(1).max(255).nullable(),
  companyId: z.string().min(1).max(255).nullable(),
});

type FormValues = z.infer<typeof formSchema>;

type Props = {
  initialValues: {
    userId: string;
    companyId: string;
  };
  users: CompanyUser[];
  children?: (
    option: { value: string; label: string } | undefined,
  ) => React.ReactNode;
  className?: React.HTMLAttributes<HTMLButtonElement>["className"];
};

export const CompanyOwnerForm = ({
  initialValues,
  users,
  children,
  className,
}: Props) => {
  const [updateCompanyUser] = useUpdateCompanyUser();
  const [userId, setUserId] = useState<string>(initialValues.userId);

  const { t } = useTranslation();

  const handleSubmit = async ({ companyId, userId }: FormValues) => {
    if (!companyId || !userId) {
      return;
    }

    await updateCompanyUser(companyId, userId, CompanyRole.OWNER);
  };

  const options = useMemo(
    () =>
      users
        .filter(
          (user) =>
            user.name &&
            user.systemRole !== SystemRole.SUPER_ADMIN &&
            getUserTypeFromCompanyRole(user.companyRole) === "accounting_firm",
        )
        .map((user) => ({
          value: user.userId,
          label: user.name,
        })),
    [users],
  );

  const handleValueChange = (value: string) => {
    setUserId(value);
    handleSubmit({ companyId: initialValues.companyId, userId: value });
  };

  const currentOption = options.find((option) => option.value === userId);

  return (
    <div className="grid gap-2">
      <Select onValueChange={handleValueChange} defaultValue={userId}>
        <SelectTrigger
          className={cn(
            className,
            "h-12",
            "!focus:outline-none !focus:ring-0 !focus:ring-offset-0",
          )}
          tabIndex={-1}
        >
          {children ? (
            children(currentOption)
          ) : (
            <>
              {currentOption ? (
                <div className="flex flex-row gap-2 items-center max-w-[250px] w-full text-start">
                  <Avatar className="h-8 w-8">
                    <AvatarFallback>
                      {getInitials(currentOption.label)}
                    </AvatarFallback>
                  </Avatar>
                  <TypographySmall className="overflow-hidden whitespace-nowrap text-ellipsis w-full">
                    {currentOption.label}
                  </TypographySmall>
                </div>
              ) : (
                <TypographySmall>{t("no_company_owner")}</TypographySmall>
              )}
            </>
          )}
        </SelectTrigger>
        <SelectContent>
          {options.map((option) => (
            <SelectItem key={option.value} value={option.value}>
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
};
